if (!customElements.get('product-cta-bar')) {
  customElements.define(
    'product-cta-bar',
    class ProductCtaBar extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.variantInput = this.querySelector('.product-variant-id');
        this.quantityInput = this.querySelector('input[name="quantity"]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.buttonText = this.querySelector('[data-cta-button-text]');
        this.priceElement = this.querySelector('[data-cta-price]');
        this.compareElement = this.querySelector('[data-cta-compare]');
        this.imageElement = this.querySelector('[data-cta-image]');
        this.quantityElement = this.querySelector('[data-cta-quantity]');
        this.quantityCartElement = this.querySelector('[data-cta-quantity-cart]');
        this.defaultLabel = this.buttonText ? this.buttonText.textContent : '';
        this.unsubscribers = [];

        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onMainQuantityChange = this.onMainQuantityChange.bind(this);
        document.addEventListener('submit', this.onFormSubmit, true);
        document.addEventListener('change', this.onMainQuantityChange);
        document.addEventListener('input', this.onMainQuantityChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this)));
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this)));
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.quantityUpdate, this.syncQuantityLabel.bind(this)));
        }

        this.syncQuantityFromMainForm();
        this.setupStickyObserver();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onFormSubmit, true);
        document.removeEventListener('change', this.onMainQuantityChange);
        document.removeEventListener('input', this.onMainQuantityChange);
        this.unsubscribers.forEach((unsubscribe) => unsubscribe?.());
        this.stickyObserver?.disconnect();
      }

      // The bar has its own add-to-cart form, so pull the variant and quantity the
      // shopper actually picked in the main product form right before submitting.
      onFormSubmit(event) {
        if (!this.form || event.target !== this.form) return;

        const variantId = document.querySelector('product-info .product-variant-id')?.value;
        if (variantId && this.variantInput) this.variantInput.value = variantId;

        const quantity = document.querySelector('product-info input[name="quantity"]')?.value;
        if (quantity && this.quantityInput) this.quantityInput.value = quantity;
      }

      onMainQuantityChange(event) {
        if (!event.target?.matches?.('product-info input[name="quantity"]')) return;
        this.syncQuantityFromMainForm();
      }

      onVariantChange(event) {
        const variant = event?.data?.variant;
        if (!variant) return;

        if (this.variantInput) this.variantInput.value = variant.id;
        this.updatePrices(event.data.html);
        this.updateImage(variant);
        this.updateAvailability(variant);
        this.syncQuantityLabel();
      }

      onCartUpdate() {
        // product-info refreshes quantity rules after cart updates; sync shortly after.
        requestAnimationFrame(() => this.syncQuantityLabel());
        setTimeout(() => this.syncQuantityLabel(), 300);
      }

      syncQuantityFromMainForm() {
        const mainQty = document.querySelector('product-info input[name="quantity"]');
        if (mainQty && this.quantityInput) {
          this.quantityInput.value = mainQty.value || '1';
        }
        this.syncQuantityLabel();
      }

      syncQuantityLabel() {
        if (!this.quantityElement || !this.quantityCartElement) return;

        const mainQty = document.querySelector('product-info input[name="quantity"]');
        const selectedQty = Number(mainQty?.value || this.quantityInput?.value || 1);
        const cartQty = Number(mainQty?.dataset?.cartQuantity || 0);
        const suffix = this.quantityElement.dataset.inCartSuffix || 'im Warenkorb';

        // Prefer cart count when present; otherwise mirror the selected quantity so
        // choosing 2 updates the sticky bar to “Anzahl (2 im Warenkorb)”.
        const displayQty = cartQty > 0 ? cartQty : selectedQty;

        this.quantityCartElement.innerHTML = `(<span class="quantity-cart">${displayQty}</span> ${suffix})`;
        this.quantityCartElement.hidden = false;
      }

      updatePrices(html) {
        const priceContainer = html?.querySelector('.product__info-container .price');
        if (!priceContainer) return;

        const onSale = priceContainer.classList.contains('price--on-sale');
        const currentPrice = onSale
          ? priceContainer.querySelector('.price__sale .price-item--sale')
          : priceContainer.querySelector('.price__regular .price-item--regular');

        if (this.priceElement && currentPrice) {
          this.priceElement.textContent = currentPrice.textContent.trim();
        }

        if (this.compareElement) {
          const comparePrice = onSale ? priceContainer.querySelector('.price__sale s.price-item--regular') : null;
          if (comparePrice) {
            this.compareElement.textContent = comparePrice.textContent.trim();
            this.compareElement.hidden = false;
          } else {
            this.compareElement.hidden = true;
          }
        }
      }

      updateImage(variant) {
        if (!this.imageElement || this.imageElement.dataset.autoUpdate !== 'true') return;
        if (!variant.featured_image?.src) return;

        const width = this.imageElement.getAttribute('width') || 200;
        this.imageElement.src = `${variant.featured_image.src}&width=${width * 2}`;
        this.imageElement.srcset = '';
      }

      updateAvailability(variant) {
        if (!this.submitButton) return;

        if (variant.available) {
          this.submitButton.removeAttribute('disabled');
          if (this.buttonText) this.buttonText.textContent = this.defaultLabel;
        } else {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (this.buttonText) this.buttonText.textContent = window.variantStrings?.soldOut || this.defaultLabel;
        }
      }

      setupStickyObserver() {
        const section = this.closest('.product-cta-bar-section');
        const shopifySection = this.closest('.shopify-section');
        const trigger =
          document.querySelector('.product-buybox-actions .product-form__submit') ||
          document.querySelector('.product-buybox-actions') ||
          document.querySelector('product-info .product-form__submit');

        if (!section || !trigger || typeof IntersectionObserver === 'undefined') return;

        section.classList.add('product-cta-bar-section--sticky');
        shopifySection?.classList.add('product-cta-bar-shopify-section--sticky');

        if (window.Shopify?.designMode) {
          section.classList.add('is-visible');
          return;
        }

        this.stickyObserver = new IntersectionObserver(
          ([entry]) => {
            section.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          },
          { threshold: 0 }
        );

        this.stickyObserver.observe(trigger);
      }
    }
  );
}
